import React, { useCallback, useMemo, useState } from 'react';
import { Button, List, message, Modal, Row, Select, Tag } from 'antd';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { ROLE_TYPES } from '../../utils/DataConstants';
import { getContractorOrWorkOrderId } from '../../utils/WorkOrderUtils';
import { debounce } from '../../utils/PerformanceUtils';
import { ContactAvatar } from '../contact_avatar/ContactAvatar';
import AvatarGroup from '../avatar_group/AvatarGroup';
import RemoveSubscriberButton from './RemoveSubscriberButton';

const { Option } = Select;

const style = require('../supplier_work_orders_detail_notes_page/SupplierWorkOrdersDetailNotesPage.less');

const secondaryFieldStyle = { fontSize: '1.15em', color: '#606060' };
const primaryFieldStyle = { fontSize: '1.2em', color: 'black' };

export interface WorkOrderNotesSubscribersPanelProps {
	workOrder: any;
	workOrderId: string | number;
	subscribers: any[];
	filteredUsers: any[];
	fetchUsers: (search: any, woId: any, filters: any, workOrder: any) => any;
	addBuyerSubscribers: (obj: any, workOrder: any) => Promise<any>;
	addSupplierSubscribers: (obj: any, workOrder: any) => Promise<any>;
	removeBuyerSubscriber?: (obj: any, workOrder: any) => Promise<any>;
	removeSupplierSubscriber: (obj: any, workOrder: any) => Promise<any>;
	getWorkOrder: (id: string) => Promise<any>;
	match: { params: { id: string } };
	avatarRowClassName?: string;
	addMemberFilledIcon?: React.ReactNode;
	/** Teal circle + white "+N" — internal / subcontractor notes only, not client-facing notes */
	useInternalNotesOverflowStyle?: boolean;
	isInternalWorkspace?: boolean;
}

const WorkOrderNotesSubscribersPanel = ({
	workOrder,
	workOrderId,
	subscribers,
	filteredUsers,
	fetchUsers,
	addBuyerSubscribers,
	addSupplierSubscribers,
	removeBuyerSubscriber,
	removeSupplierSubscriber,
	getWorkOrder,
	match,
	avatarRowClassName,
	addMemberFilledIcon,
	useInternalNotesOverflowStyle,
	isInternalWorkspace = false,
}: WorkOrderNotesSubscribersPanelProps) => {
	const [subscriberModalVisible, setSubscriberModalVisible] = useState(false);
	const [addSubscriberModalVisible, setAddSubscriberModalVisible] = useState(false);
	const [search, setSearch] = useState('');
	const [selectedUsers, setSelectedUsers] = useState<any[]>([]);
	const [selectValue, setSelectValue] = useState<any[]>([]);
	const [addingSubscribers, setAddingSubscribers] = useState(false);
	const [removingSubscriberEmail, setRemovingSubscriberEmail] = useState<string | null>(null);

	const debouncedCallback = useMemo(
		() =>
			debounce(
				(searchText) => {
					fetchUsers(
						searchText && searchText.trim(),
						getContractorOrWorkOrderId(workOrder),
						{},
						workOrder
					);
				},
				400,
				false
			),
		[fetchUsers, workOrder]
	);

	const handleAddSubscriberSearchDebounced = useCallback(
		(value = '') => {
			const searchText = value;
			setSearch(searchText && searchText.trim());
			debouncedCallback(searchText);
		},
		[debouncedCallback]
	);

	const handleOnChange = useCallback((val) => {
		const updatedVal = Array.isArray(val) ? val.map((v) => JSON.parse(v)) : JSON.parse(val);
		setSelectValue(updatedVal.map((v) => JSON.stringify(v)));
		setSelectedUsers(updatedVal);
	}, []);

	const handleAddSubscribers = useCallback(() => {
		const buyerKey = nullSafeGet('contractorWo.id', workOrder)
			? 'addContractorSubscriberEmails'
			: 'addBuyerSubscriberEmails';
		const supplierKey = nullSafeGet('contractorWo.id', workOrder)
			? 'addSubSubscriberEmails'
			: 'addSupplierSubscriberEmails';

		const objBuyer = {
			id: getContractorOrWorkOrderId(workOrder),
			[buyerKey]: selectedUsers
				.filter((user) => user.contactType === 'buyer')
				.map((user) => user.email),
		};
		const objSupplier = {
			id: getContractorOrWorkOrderId(workOrder),
			[supplierKey]: selectedUsers
				.filter((user) => user.contactType === 'supplier')
				.map((user) => user.email),
		};
		const subscribersAddition = [
			...(Array.isArray(objBuyer[buyerKey]) && objBuyer[buyerKey].length > 0
				? [addBuyerSubscribers(objBuyer, workOrder)]
				: []),
			...(Array.isArray(objSupplier[supplierKey]) && objSupplier[supplierKey].length > 0
				? [addSupplierSubscribers(objSupplier, workOrder)]
				: []),
		];
		setAddingSubscribers(true);
		Promise.all(subscribersAddition)
			.then(() => {
				return getWorkOrder(match.params.id).then(() => {
					setSelectValue([]);
					setAddSubscriberModalVisible(false);
				});
			})
			.catch(() => {})
			.finally(() => {
				setAddingSubscribers(false);
			});
	}, [
		addBuyerSubscribers,
		addSupplierSubscribers,
		getWorkOrder,
		match.params.id,
		selectedUsers,
		workOrder,
	]);

	const handleSubscriberRemoved = useCallback(
		(item) => () => {
			const email = item && item.email ? String(item.email) : null;
			setRemovingSubscriberEmail(email);
			const key = nullSafeGet('contractorWo.id', workOrder)
				? 'removeSubSubscriberEmails'
				: 'removeSupplierSubscriberEmails';
			if (item.contactType === ROLE_TYPES.BUYER) {
				const obj = {
					id: getContractorOrWorkOrderId(workOrder),
					removeBuyerSubscriberEmails: [item.email],
				};
				if (removeBuyerSubscriber) {
					removeBuyerSubscriber(obj, workOrder)
						.then(() => {
							return getWorkOrder(match.params.id).then(() => {
								setRemovingSubscriberEmail(null);
							});
						})
						.catch(() => {
							setRemovingSubscriberEmail(null);
							message.error('Failed to remove subscriber. Please try again.');
						});
				}
			} else {
				const obj = {
					id: getContractorOrWorkOrderId(workOrder),
					[key]: [item.email],
				};
				removeSupplierSubscriber(obj, workOrder)
					.then(() => {
						return getWorkOrder(match.params.id).then(() => {
							setRemovingSubscriberEmail(null);
						});
					})
					.catch(() => {
						setRemovingSubscriberEmail(null);
						message.error('Failed to remove subscriber. Please try again.');
					});
			}
			const clearIfSame = () => {
				setRemovingSubscriberEmail((prev) => {
					return prev && email && String(prev).toLowerCase() === String(email).toLowerCase()
						? null
						: prev;
				});
			};
			setTimeout(clearIfSame, 5000);
		},
		[getWorkOrder, match.params.id, removeBuyerSubscriber, removeSupplierSubscriber, workOrder]
	);

	const optionStyleSupplier = {
		backgroundColor: 'rgb(37, 150, 190,0.05)',
		color: 'red',
		'&:hover': {
			backgroundColor: 'rgb(37, 150, 190,0.15)',
		},
	};
	const optionStyleBuyer = {
		backgroundColor: 'rgb(124, 72, 189,0.05)',
		'&:hover': {
			backgroundColor: 'rgb(124, 72, 189,0.15)',
		},
	};

	return (
		<>
			<div className="text-right" style={{ padding: 0, height: 0 }}>
				<Modal
					width={600}
					title={
						<div>
							<span>Work Order {workOrderId}</span>
							<Button
								style={{ float: 'right', marginRight: '32px', marginTop: '-4px' }}
								onClick={() => {
									setAddSubscriberModalVisible(true);
									setSearch('');
									setSelectedUsers([]);
								}}
								type={'primary'}
							>
								Add People
							</Button>
						</div>
					}
					footer={null}
					closable={true}
					onCancel={() => setSubscriberModalVisible(false)}
					visible={subscriberModalVisible}
				>
					<List
						itemLayout="horizontal"
						dataSource={subscribers}
						renderItem={(item) => (
							<List.Item>
								<List.Item.Meta
									avatar={
										<ContactAvatar
											hidePopover={true}
											placement="bottomRight"
											userType="supplier"
											contact={item}
										/>
									}
									title={`${item.nameGiven} ${item.nameFamily}`}
									description={item.email}
								/>
								<RemoveSubscriberButton
									subscriber={item}
									onRemove={handleSubscriberRemoved}
									workOrder={workOrder}
									loading={
										!!(
											removingSubscriberEmail &&
											String(removingSubscriberEmail).toLowerCase() ===
												String(item.email || '').toLowerCase()
										)
									}
								/>
							</List.Item>
						)}
					/>
				</Modal>
				<Modal
					width={600}
					title={
						<div>
							<span>
								{isInternalWorkspace
									? 'Add Someone to Internal Notes for Work Order'
									: 'Add Someone to Work Order'}{' '}
								{workOrderId}
							</span>
						</div>
					}
					footer={
						<div style={{ display: 'block', width: '100%' }}>
							<Button loading={addingSubscribers} onClick={handleAddSubscribers} type={'primary'}>
								Add Subscribers
							</Button>
						</div>
					}
					closable={true}
					onCancel={() => setAddSubscriberModalVisible(false)}
					visible={addSubscriberModalVisible}
				>
					<Row key={0}>
						<Select
							mode="multiple"
							style={{ width: '100%' }}
							placeholder="Begin to type a name or email to start searching"
							onSearch={handleAddSubscriberSearchDebounced}
							allowClear={true}
							autoClearSearchValue={true}
							onFocus={() => {
								handleAddSubscriberSearchDebounced();
							}}
							onChange={handleOnChange}
							autoFocus={true}
							value={selectValue}
							optionLabelProp="label"
							dropdownClassName={style.subscriberDropdown}
							filterOption={() => true}
							notFoundContent={
								search.length > 0 ? (
									search.includes('@') ? (
										search.slice(search.indexOf('@'), search.length).includes('.') ? (
											<Option key={search} value={search}>
												<div style={{ display: 'inline-block' }}>eMail Icon</div>
												<div style={{ display: 'inline-block' }}>
													Invite {search} to join OpenWrench
												</div>
											</Option>
										) : (
											<div>Sorry! We couldn't find this user. Try searching by name!</div>
										)
									) : (
										<div>Sorry! We couldn't find this user. Try searching by email!</div>
									)
								) : null
							}
						>
							{filteredUsers.length > 0
								? filteredUsers.map((user) => (
										<Option
											disabled={subscribers.some(
												(u) =>
													nullSafeGetOrElse('email', u, '').toLowerCase() ===
													nullSafeGetOrElse('email', user, '').toLowerCase()
											)}
											key={user.email}
											value={JSON.stringify(user)}
											title={user.nameGiven.concat(' ', user.nameFamily)}
											{...{ label: user.nameGiven.concat(' ', user.nameFamily) }}
											style={
												user.contactType === 'supplier' ? optionStyleSupplier : optionStyleBuyer
											}
											className={
												user.contactType === 'supplier'
													? style.subscriberOptionSupplier
													: style.subscriberOptionBuyer
											}
										>
											<ContactAvatar
												hidePopover={true}
												placement="bottomRight"
												userType="supplier"
												contact={user}
												className={'contactAvatar'}
												style={{
													marginBottom: '6px',
													backgroundColor: user.contactType === 'supplier' ? '#48a7bd' : '#7c48bd',
												}}
											/>
											<div style={{ display: 'inline-block', verticalAlign: 'middle' }}>
												<div style={{ display: 'inline-block', paddingLeft: '12px' }}>
													<div style={{ display: 'block' }}>
														<div
															style={search.includes('@') ? secondaryFieldStyle : primaryFieldStyle}
														>
															{user.nameGiven} {user.nameFamily}
															{subscribers.some(
																(u) =>
																	nullSafeGetOrElse('email', u, '').toLowerCase() ===
																	nullSafeGetOrElse('email', user, '').toLowerCase()
															) && (
																<span className="px-2">
																	<Tag>Already in this channel</Tag>
																</span>
															)}
														</div>
														<div
															style={search.includes('@') ? primaryFieldStyle : secondaryFieldStyle}
														>
															{user.email}
														</div>
													</div>
												</div>
											</div>
										</Option>
								  ))
								: null}
						</Select>
					</Row>
				</Modal>
			</div>
			<div className={avatarRowClassName || 'flex flex-row justify-end pr-4'}>
				<AvatarGroup
					userType="supplier"
					avatars={subscribers}
					onClick={() => setSubscriberModalVisible(true)}
					onAddClick={() => {
						setAddSubscriberModalVisible(true);
						setSearch('');
						setSelectedUsers([]);
					}}
					addMemberFilledIcon={addMemberFilledIcon}
					useInternalNotesOverflowStyle={useInternalNotesOverflowStyle}
				/>
			</div>
		</>
	);
};

export default WorkOrderNotesSubscribersPanel;
